import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { CheckCircle, Circle, ArrowRight, X, Lightning } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const DISMISS_KEY = "hc_onboarding_dismissed";

/**
 * First-week activation checklist — pinned at the top of the home feed for
 * users who haven't completed all 5 setup actions. Drives the highest-leverage
 * actions that turn a signup into an actual community member.
 *
 * Steps (in order):
 *   1. Verify email           → email_verified
 *   2. Set an avatar          → user.picture present
 *   3. Add a bio + 3 skills   → bio non-empty AND skills.length >= 3
 *   4. Solve a lab            → labs_solved > 0 (lightweight aggregate)
 *   5. Follow 3 hackers       → following_count >= 3
 *
 * Hidden if all 5 are complete OR the user explicitly dismissed.
 */
export default function OnboardingChecklist() {
    const { user } = useAuth();
    const [dismissed, setDismissed] = useState(() => {
        try { return localStorage.getItem(DISMISS_KEY) === "1"; } catch { return false; }
    });
    const [progress, setProgress] = useState(null);

    useEffect(() => {
        if (!user || dismissed) return;
        let cancelled = false;
        (async () => {
            try {
                // Pull the latest /auth/me + counts in parallel. /auth/me already has skills/bio/picture/email_verified.
                const [me, follows, labs] = await Promise.all([
                    api.get("/auth/me"),
                    api.get(`/users/${user.username}/follow-stats`).catch(() => ({ data: { following: 0 } })),
                    api.get("/me/labs/solved-count").catch(() => ({ data: { count: 0 } })),
                ]);
                if (cancelled) return;
                const m = me.data || {};
                setProgress({
                    email_verified: m.email_verified !== false,
                    has_avatar: Boolean(m.picture),
                    has_profile: Boolean((m.bio || "").trim()) && (m.skills || []).length >= 3,
                    has_lab: (labs.data?.count || 0) > 0,
                    follows_three: (follows.data?.following || 0) >= 3,
                });
            } catch {
                // If any of the calls fail, just hide the checklist (no nag).
                if (!cancelled) setProgress(null);
            }
        })();
        return () => { cancelled = true; };
    }, [user, dismissed]);

    if (!user || dismissed || !progress) return null;

    const steps = [
        { key: "email_verified", done: progress.email_verified, label: "Verify your email", to: "/settings#account", desc: "Unlock the full community." },
        { key: "has_avatar", done: progress.has_avatar, label: "Add a profile picture", to: "/settings#profile", desc: "Doubles the chance a recruiter clicks." },
        { key: "has_profile", done: progress.has_profile, label: "Write a bio & add 3 skills", to: "/settings#profile", desc: "Show up in talent search." },
        { key: "has_lab", done: progress.has_lab, label: "Solve your first lab", to: "/labs", desc: "Earn XP and the first-blood badge." },
        { key: "follows_three", done: progress.follows_three, label: "Follow 3 hackers", to: "/leaderboard", desc: "Get a feed of their answers + questions." },
    ];
    const completed = steps.filter((s) => s.done).length;
    if (completed === steps.length) return null;

    const dismiss = () => {
        try { localStorage.setItem(DISMISS_KEY, "1"); } catch { /* noop */ }
        setDismissed(true);
    };

    const pct = Math.round((completed / steps.length) * 100);

    return (
        <div className="relative border border-cyan-500/30 bg-cyan-500/[0.04] rounded-sm p-4 sm:p-5 mb-6" data-testid="onboarding-checklist">
            <button
                onClick={dismiss}
                aria-label="Dismiss onboarding"
                className="absolute top-3 right-3 text-slate-500 hover:text-slate-200 transition-colors"
                data-testid="onboarding-dismiss"
            >
                <X size={14} weight="bold" />
            </button>
            <div className="flex items-center gap-2 mb-1">
                <Lightning size={14} weight="duotone" className="text-cyan-300" />
                <h2 className="text-[10px] uppercase tracking-widest text-cyan-300 font-mono" data-testid="profile-setup-heading">// profile setup · {completed}/{steps.length} done</h2>
            </div>
            <h3 className="text-base sm:text-lg font-bold text-slate-100" style={{ fontFamily: "'Space Grotesk', 'Chivo', sans-serif" }}>
                {pct < 50 ? "Complete your setup" : "Almost there — keep going"}
            </h3>
            <p className="text-slate-400 text-xs sm:text-sm mt-0.5 mb-3 font-mono">
                Each step makes you more discoverable and unlocks features.
            </p>
            {/* Progress bar */}
            <div className="h-1 bg-slate-800 rounded-full overflow-hidden mb-4">
                <div className="h-full bg-gradient-to-r from-cyan-400 to-emerald-400 transition-all duration-500" style={{ width: `${pct}%` }} />
            </div>
            <ul className="space-y-1.5">
                {steps.map((s) => (
                    <li key={s.key} data-testid={`onboarding-step-${s.key}`}>
                        <Link
                            to={s.to}
                            className={`flex items-center gap-3 rounded-sm px-2 py-1.5 -mx-2 transition-colors ${s.done ? "opacity-60" : "hover:bg-slate-800/40"}`}
                        >
                            {s.done
                                ? <CheckCircle size={18} weight="fill" className="text-emerald-400 shrink-0" />
                                : <Circle size={18} weight="bold" className="text-slate-600 shrink-0" />}
                            <div className="flex-1 min-w-0">
                                <div className={`text-sm font-medium ${s.done ? "text-slate-500 line-through" : "text-slate-100"}`}>{s.label}</div>
                                {!s.done && <div className="text-[11px] font-mono text-slate-500 mt-0.5">{s.desc}</div>}
                            </div>
                            {!s.done && <ArrowRight size={13} className="text-slate-500 shrink-0" />}
                        </Link>
                    </li>
                ))}
            </ul>
        </div>
    );
}
